import { type ReactNode, useEffect, useState } from "react";
import type { AnalyticsState } from "../analytics.js";
import { useFeatures } from "../features.js";
import { useI18n } from "../i18n/index.js";
import { useLegalLinks } from "../legal-links.js";
import { Link } from "../navigation.js";
import { PATHS } from "../router.js";
import { LINKS } from "../site.js";
import { BrandLogo } from "./brand.js";
import { ConsentBanner } from "./consent-banner.js";
import { LanguageSwitcher } from "./language-switcher.js";
import styles from "./layout.module.css";
import { cx } from "./ui.js";
import { UserMenu } from "./user-menu.js";

export function Layout(props: {
  readonly children: ReactNode;
  /** A page wider than the others, with the header and the footer as wide as it. */
  readonly wide?: boolean;
}) {
  const { t } = useI18n();
  const features = useFeatures();
  // The deployment's own pages are written into the head by the server, so they are known only
  // once the page is up: until then, as in prerendered markup, the footer has none.
  const legal = useLegalLinks() ?? {};
  // So is what the bootstrap left about analytics: the footer offers the choice once the page is
  // up, to everyone who has one to make (ADR-0051).
  const [analytics, setAnalytics] = useState<AnalyticsState | undefined>(undefined);
  const [choices, setChoices] = useState(false);
  useEffect(() => {
    setAnalytics(window.skillcdnAnalytics);
  }, []);
  const links = [
    ...(legal.termsUrl === undefined ? [] : [{ href: legal.termsUrl, label: t.footer.terms }]),
    ...(legal.privacyUrl === undefined
      ? []
      : [{ href: legal.privacyUrl, label: t.footer.privacy }]),
    ...(legal.contactEmail === undefined
      ? []
      : [{ href: `mailto:${legal.contactEmail}`, label: t.footer.report }]),
  ];
  const offersChoice = analytics !== undefined && !analytics.refused;
  return (
    <div className={cx(styles.page, props.wide === true && styles.wide)}>
      {/* The field and the far light. It is an element and not another background layer on the
          page because it has to move at its own speed, and only a transform moves without asking
          the main thread for a repaint on every frame. */}
      <div className={styles.room} aria-hidden="true" />
      <a className={styles.skip} href="#content">
        {t.nav.skipToContent}
      </a>
      <header className={styles.header}>
        <div className={styles.bar}>
          <Link className={styles.brand} href={PATHS.landing} aria-label={t.nav.home}>
            <BrandLogo className={styles.logo} />
          </Link>
          <nav className={styles.nav} aria-label={t.nav.main}>
            <Link href={PATHS.explore}>{t.nav.explore}</Link>
            {features.search && <Link href={PATHS.search}>{t.nav.search}</Link>}
            <Link href={PATHS.docs}>{t.nav.docs}</Link>
            <a href={LINKS.repository}>{t.nav.github}</a>
          </nav>
          <div className={styles.controls}>
            <LanguageSwitcher />
            <UserMenu />
          </div>
        </div>
      </header>
      <main id="content" className={styles.main}>
        {props.children}
      </main>
      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          {/* The name and the deployment's own pages share one line, at the two ends of the
              page: its terms, its privacy policy and whom to write to, each only where the
              deployment has it. What the site promises sits under them, quietly. */}
          <div className={styles.footerTop}>
            <p className={styles.footerBrand}>
              <BrandLogo className={styles.logo} title={t.meta.siteName} />
            </p>
            {(links.length > 0 || offersChoice) && (
              <ul className={styles.footerLinks}>
                {links.map((link) => (
                  <li key={link.href}>
                    <a href={link.href}>{link.label}</a>
                  </li>
                ))}
                {offersChoice && (
                  <li>
                    <button type="button" onClick={() => setChoices(true)}>
                      {t.consent.choices}
                    </button>
                  </li>
                )}
              </ul>
            )}
          </div>
          <p className={styles.footerNote}>{t.footer.tagline}</p>
        </div>
      </footer>
      <ConsentBanner analytics={analytics} choices={choices} onClose={() => setChoices(false)} />
    </div>
  );
}
